import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './AdminUsers.module.scss';
import { AdminRoleDto } from '../../../types/admin.types';
import {
    AdminUserMutationError,
    createAdminUser,
    fetchAdminRoles
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import {
    ADMIN_USER_ACCESS_ROLES,
    AdminUserAccessRole,
    isAdminUserAccessRole,
    isSuperAdmin,
    ROLE_ADMINISTRATOR,
    ROLE_CONTENT_PROCESSOR_SERVICE,
    ROLE_MEMBER,
    ROLE_SUPER_ADMINISTRATOR
} from '../../../utils/roles';
import { Routes } from '../../../types/constants';
import { useAdminUnsavedChanges } from './useAdminUnsavedChanges';

const elevatedRoles = new Set([ROLE_ADMINISTRATOR, ROLE_SUPER_ADMINISTRATOR, ROLE_CONTENT_PROCESSOR_SERVICE]);
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isProtectedRole = (roleName: string) => (
    roleName.toLowerCase() === ROLE_SUPER_ADMINISTRATOR.toLowerCase()
    || roleName.toLowerCase() === ROLE_CONTENT_PROCESSOR_SERVICE.toLowerCase()
);

const AdminUserCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const usersReturnTo = ((location.state ?? {}) as { usersReturnTo?: string }).usersReturnTo ?? Routes.ADMIN_USERS;
    const { getUserInfo } = useUserState();
    const currentUser = getUserInfo();
    const token = currentUser?.token;
    const canManageProtectedRoles = isSuperAdmin(currentUser?.roles);
    const [roles, setRoles] = useState<AdminRoleDto[]>([]);
    const [selectedAccessRole, setSelectedAccessRole] = useState<AdminUserAccessRole>(ROLE_MEMBER);
    const [model, setModel] = useState({ userName: '', firstName: '', lastName: '', email: '', enabled: true });
    const [isLoadingRoles, setIsLoadingRoles] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const isDirty = Boolean(
        model.userName || model.firstName || model.lastName || model.email
        || !model.enabled || selectedAccessRole !== ROLE_MEMBER
    );
    useAdminUnsavedChanges(isDirty && !isSaving);

    useEffect(() => {
        let active = true;
        setIsLoadingRoles(true);
        fetchAdminRoles(token)
            .then((response) => {
                if (!active) {
                    return;
                }
                setRoles(response
                    .map((role: any) => ({ id: role.id ?? role.Id ?? '', name: role.name ?? role.Name ?? '' }))
                    .filter((role) => role.name
                        && isAdminUserAccessRole(role.name)
                        && (canManageProtectedRoles || !isProtectedRole(role.name))));
            })
            .catch(() => {
                if (active) {
                    setErrorMessage('Unable to load roles. User creation is unavailable until roles can be verified.');
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoadingRoles(false);
                }
            });
        return () => {
            active = false;
        };
    }, [canManageProtectedRoles, token]);

    const roleOptions = useMemo(() => {
        const available = roles.some((role) => role.name === ROLE_MEMBER)
            ? roles
            : [{ id: ROLE_MEMBER, name: ROLE_MEMBER }, ...roles];
        return [...available].sort((left, right) => (
            ADMIN_USER_ACCESS_ROLES.indexOf(left.name as AdminUserAccessRole)
            - ADMIN_USER_ACCESS_ROLES.indexOf(right.name as AdminUserAccessRole)
        ));
    }, [roles]);

    const validate = () => {
        const nextErrors: Record<string, string> = {};
        if (!model.userName.trim()) nextErrors.userName = 'Username is required.';
        if (!model.firstName.trim()) nextErrors.firstName = 'First name is required.';
        if (!model.lastName.trim()) nextErrors.lastName = 'Last name is required.';
        if (!model.email.trim()) nextErrors.email = 'Email is required.';
        else if (!emailPattern.test(model.email.trim())) nextErrors.email = 'Enter a valid email address.';
        setFieldErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        if (!validate()) {
            return;
        }
        if (elevatedRoles.has(selectedAccessRole)
            && !window.confirm('Create this user with elevated access?')) {
            return;
        }

        setIsSaving(true);
        try {
            const created = await createAdminUser({
                userName: model.userName.trim(),
                firstName: model.firstName.trim(),
                lastName: model.lastName.trim(),
                email: model.email.trim(),
                roles: selectedAccessRole === ROLE_MEMBER
                    ? [ROLE_MEMBER]
                    : [ROLE_MEMBER, selectedAccessRole],
                enabled: model.enabled
            }, token);
            const destination = created.userId
                ? `${Routes.ADMIN_USERS}/${encodeURIComponent(created.userId)}`
                : usersReturnTo;
            navigate(destination, {
                replace: true,
                state: {
                    actionMessage: `Created ${created.username}. Setup email sent to ${created.email}.`,
                    usersReturnTo
                }
            });
        } catch (error) {
            if (error instanceof AdminUserMutationError) {
                setErrorMessage(error.message);
                setFieldErrors(error.fieldErrors);
            } else {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to create user.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.workspaceHeader}>
                <div>
                    <button className={styles.backButton} type="button" onClick={() => navigate(usersReturnTo)}>← Back to users</button>
                    <div className={styles.userHero}>
                        <span className={styles.heroAvatar} aria-hidden="true">+</span>
                        <div>
                            <h1>Create user</h1>
                            <p>Set up identity and initial access in a clear, reviewable workflow.</p>
                        </div>
                    </div>
                </div>
            </header>

            {errorMessage ? <div className={styles.errorBanner} role="alert">{errorMessage}</div> : null}
            <div className={styles.infoBanner}>
                Creating the account generates a temporary password and sends a setup email. The user must reset their password before first login.
            </div>

            <form className={styles.workspaceGrid} onSubmit={handleSubmit} noValidate>
                <section className={`${styles.workspaceSection} ${styles.workspaceSectionFull}`}>
                    <h2>Identity</h2>
                    <p className={styles.sectionLead}>These details identify the account and determine where the setup email is sent.</p>
                    <div className={styles.formGrid}>
                        <label className={styles.formField}>
                            Username
                            <input
                                value={model.userName}
                                onChange={(event) => {
                                    setModel({ ...model, userName: event.target.value });
                                    setFieldErrors((current) => ({ ...current, userName: '' }));
                                }}
                                maxLength={256}
                                autoComplete="off"
                                aria-invalid={Boolean(fieldErrors.userName)}
                            />
                            {fieldErrors.userName ? <span className={styles.fieldError}>{fieldErrors.userName}</span> : null}
                        </label>
                        <label className={styles.formField}>
                            Email
                            <input
                                type="email"
                                value={model.email}
                                onChange={(event) => {
                                    setModel({ ...model, email: event.target.value });
                                    setFieldErrors((current) => ({ ...current, email: '' }));
                                }}
                                maxLength={256}
                                autoComplete="off"
                                aria-invalid={Boolean(fieldErrors.email)}
                            />
                            {fieldErrors.email ? <span className={styles.fieldError}>{fieldErrors.email}</span> : null}
                        </label>
                        <label className={styles.formField}>
                            First name
                            <input
                                value={model.firstName}
                                onChange={(event) => {
                                    setModel({ ...model, firstName: event.target.value });
                                    setFieldErrors((current) => ({ ...current, firstName: '' }));
                                }}
                                maxLength={256}
                                aria-invalid={Boolean(fieldErrors.firstName)}
                            />
                            {fieldErrors.firstName ? <span className={styles.fieldError}>{fieldErrors.firstName}</span> : null}
                        </label>
                        <label className={styles.formField}>
                            Last name
                            <input
                                value={model.lastName}
                                onChange={(event) => {
                                    setModel({ ...model, lastName: event.target.value });
                                    setFieldErrors((current) => ({ ...current, lastName: '' }));
                                }}
                                maxLength={256}
                                aria-invalid={Boolean(fieldErrors.lastName)}
                            />
                            {fieldErrors.lastName ? <span className={styles.fieldError}>{fieldErrors.lastName}</span> : null}
                        </label>
                    </div>
                </section>

                <section className={`${styles.workspaceSection} ${styles.workspaceSectionFull}`}>
                    <h2>Initial access</h2>
                    <p className={styles.sectionLead}>Every account remains a Member. Choose one access level; protected service and SuperAdministrator access is available only to SuperAdministrators.</p>
                    {isLoadingRoles ? <p className={styles.sectionLead}>Loading roles…</p> : (
                        <label className={styles.formField}>
                            Access level
                            <select
                                value={selectedAccessRole}
                                onChange={(event) => {
                                    if (isAdminUserAccessRole(event.target.value)) {
                                        setSelectedAccessRole(event.target.value);
                                    }
                                }}
                            >
                                {roleOptions.map((role) => (
                                    <option key={role.id || role.name} value={role.name}>{role.name}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className={styles.checkboxField}>
                        <input type="checkbox" checked={model.enabled} onChange={(event) => setModel({ ...model, enabled: event.target.checked })} />
                        <span><strong>Enable account</strong><br />Disabled users receive setup email but cannot sign in until an administrator enables them.</span>
                    </label>
                    <div className={styles.formActions}>
                        <button className={styles.primaryButton} type="submit" disabled={isSaving || isLoadingRoles || Boolean(errorMessage && roles.length === 0)}>
                            {isSaving ? 'Creating user…' : 'Create user and send setup email'}
                        </button>
                        <button className={styles.secondaryButton} type="button" onClick={() => navigate(usersReturnTo)}>Cancel</button>
                    </div>
                </section>
            </form>
        </div>
    );
};

export default AdminUserCreate;
